"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";

const typographyVariants = cva("text-dark transition-colors", {
  variants: {
    variant: {
      h1: "scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl",
      h2: "scroll-m-20 text-3xl font-semibold tracking-tight transition-colors first:mt-0",
      h3: "scroll-m-20 text-2xl font-semibold tracking-tight",
      h4: "scroll-m-20 text-xl font-semibold tracking-tight",
      p: "leading-7",
      span: "leading-6",
      blockquote: "mt-6 border-l-2 pl-6 italic",
      lead: "text-xl text-muted-foreground",
      large: "text-lg font-semibold",
      small: "text-sm font-medium leading-none",
      muted: "text-sm text-muted-foreground",
      code: "relative rounded bg-accent/10 px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold",
    },
    weight: {
      light: "font-light",
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
      extrabold: "font-extrabold",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
      justify: "text-justify",
    },
    transition: {
      true: "transition-colors duration-200",
    },
    font: {
      sans: 'font-sans',
      serif: 'font-serif',
      mono: 'font-mono',
      display: 'font-display',
      body: 'font-body',
    },
  },
  defaultVariants: {
    variant: "p",
    weight: "normal",
    transition: false,
    font: "body",
  },
});

type TypographyProps<T extends React.ElementType> = {
  as?: T;
  asChild?: boolean;
} & Omit<React.ComponentPropsWithoutRef<T>, "as" | "asChild"> &
  VariantProps<typeof typographyVariants>;

const Typography = <T extends React.ElementType = "p">({
  className,
  variant,
  weight,
  font,
  align,
  transition,
  as,
  asChild = false,
  ...props
}: TypographyProps<T>) => {
  const Comp = asChild ? Slot : as || "p";

  return (
    <Comp
      className={cn(
        typographyVariants({ variant, font, weight, align, transition, className })
      )}
      {...props}
    />
  );
};

Typography.displayName = "Typography";

export { Typography, typographyVariants };
